<script setup lang="ts">
import Favico from 'favico.js';

import { useBotStore } from '@/stores/ftbotwrapper';
import { useLayoutStore } from '@/stores/layout';
import { OpenTradeVizOptions, useSettingsStore } from '@/stores/settings';
import { breakpointsTailwind } from '@vueuse/core';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import IconHome from '~icons/mdi/home-outline';
import IconStrategies from '~icons/mdi/view-dashboard-outline';
import IconGenerator from '~icons/mdi/creation-outline';
import IconIndicators from '~icons/mdi/chart-bell-curve';
import IconTransformer from '~icons/mdi/code-braces';
import IconAiTrading from '~icons/mdi/robot-outline';
import IconBacktest from '~icons/mdi/chart-timeline-variant';

const botStore = useBotStore();
const userStore = useUserStore();
const settingsStore = useSettingsStore();
const layoutStore = useLayoutStore();
const route = useRoute();
const router = useRouter();
const favicon = ref<Favico | undefined>(undefined);
const pingInterval = ref<number>();

const breakpoints = useBreakpoints(breakpointsTailwind);

// The full link bar needs ~1000px; below that the links move into the drawer.
const isMobile = breakpoints.smaller('lg');

async function clickLogout() {
  botStore.removeBot(botStore.selectedBot);
  // TODO: This should be per bot
  await router.push('/');
}

const setOpenTradesAsPill = (tradeCount: number) => {
  if (!favicon.value) {
    favicon.value = new Favico({
      animation: 'none',
      // position: 'up',
      // fontStyle: 'normal',
      // bgColor: '#',
      // textColor: '#FFFFFF',
    });
  }
  if (tradeCount !== 0 && settingsStore.openTradesInTitle === 'showPill') {
    favicon.value.badge(tradeCount);
  } else {
    favicon.value.reset();
    console.log('reset');
  }
};
const resetDynamicLayout = (): void => {
  console.log(`resetLayout called for ${route?.fullPath}`);
  switch (route?.fullPath) {
    case '/trade':
      layoutStore.resetTradingLayout();
      break;
    case '/dashboard':
      layoutStore.resetDashboardLayout();
      break;
    default:
  }
};
const setTitle = () => {
  let title = 'freqUI';
  if (settingsStore.openTradesInTitle === OpenTradeVizOptions.asTitle) {
    title = `(${botStore.activeBotorUndefined?.openTradeCount}) ${title}`;
  }
  if (botStore.activeBotorUndefined?.botName) {
    title = `${title} - ${botStore.activeBotorUndefined?.botName}`;
  }
};

onBeforeUnmount(() => {
  if (pingInterval.value) {
    clearInterval(pingInterval.value);
  }
});

onMounted(async () => {
  userStore.fetchOnlyAiUser();
  await settingsStore.loadUIVersion();
  pingInterval.value = window.setInterval(botStore.pingAll, 60000);
});

settingsStore.$subscribe((_, state) => {
  const needsUpdate = settingsStore.openTradesInTitle !== state.openTradesInTitle;
  if (needsUpdate) {
    setTitle();
    setOpenTradesAsPill(botStore.activeBotorUndefined?.openTradeCount || 0);
  }
});

watch(
  () => botStore.activeBotorUndefined?.botName,
  () => setTitle(),
);
watch(
  () => botStore.activeBotorUndefined?.openTradeCount,
  () => {
    if (settingsStore.openTradesInTitle === OpenTradeVizOptions.showPill) {
      setOpenTradesAsPill(botStore.activeBotorUndefined?.openTradeCount ?? 0);
    } else if (settingsStore.openTradesInTitle === OpenTradeVizOptions.asTitle) {
      setTitle();
    }
  },
);

// Navigation items array
const navItems = ref([
  // {
  //   label: 'Trade',
  //   to: '/trade',
  //   visible: computed(() => !botStore.canRunBacktest),
  //   icon: 'i-mdi-currency-usd',
  // },
  // {
  //   label: 'Dashboard',
  //   to: '/dashboard',
  //   visible: computed(() => !botStore.canRunBacktest),
  //   icon: 'i-mdi-view-dashboard',
  // },
  // {
  //   label: 'Chart',
  //   to: '/graph',
  //   icon: 'i-mdi-chart-line',
  // },
  // {
  //   label: 'Logs',
  //   to: '/logs',
  //   icon: 'i-mdi-format-list-bulleted',
  // },
  // {
  //   label: 'Settings',
  //   to: '/settings',
  //   mobileOnly: true,
  //   icon: 'i-mdi-cog',
  // },
  {
    label: 'Backtest',
    to: '/backtest',
    visible: computed(() => botStore.canRunBacktest),
    icon: IconBacktest,
  },
  // {
  //   label: 'Download Data',
  //   to: '/download_data',
  //   visible: computed(
  //     () => botStore.isWebserverMode && botStore.activeBot.botFeatures.downloadDataView,
  //   ),
  //   icon: 'i-mdi-download',
  // },
  // {
  //   label: 'Pairlist Config',
  //   to: '/pairlist_config',
  //   icon: 'i-mdi-format-list-numbered-rtl',
  //   visible: computed(
  //     () =>
  //       (botStore.activeBot?.isWebserverMode ?? false) &&
  //       botStore.activeBot.botFeatures.pairlistConfig,
  //   ),
  // },
]);

const menuItems = computed<MenuItem[]>(() => [
  {
    label: `V: ${settingsStore.uiVersion}`,
    disabled: true,
  },
  {
    label: 'Settings',
    icon: 'i-mdi-cog',
    command: () => router.push('/settings'),
  },
  {
    label: 'Lock dynamic Layout',
    checkbox: true,
    checked: layoutStore.layoutLocked,
    command: () => {
      layoutStore.layoutLocked = !layoutStore.layoutLocked;
    },
  },
  {
    label: 'Reset Layout',
    icon: 'i-mdi-lock-reset',
    command: resetDynamicLayout,
  },
  {
    label: 'Logout',
    icon: 'i-mdi-logout',
    command: clickLogout,
    visible: botStore.hasBots && botStore.botCount === 1,
  },
]);
const menu = ref<InstanceType<typeof Menu> | null>();
function toggleMenu(event) {
  menu.value?.toggle(event);
}
const drawerVisible = ref(false);

const WHITELABEL_USER_ID = '7588dcc7-9d69-45ee-b6f5-959ca54c28de';
const isWhitelabel = computed(() => userStore.getUserId === WHITELABEL_USER_ID);

// Links to the other Xiomex apps, shared by the desktop bar and the phone drawer.
const externalLinks = computed(() => [
  ...(userStore.isOnlyAiUser
    ? []
    : [
        {
          label: isWhitelabel.value ? 'Oxem' : 'Xiomex',
          href: isWhitelabel.value
            ? 'https://xiomexai-whitelabel-app.vercel.app/dashboard'
            : 'https://app.xiomex.fund/dashboard',
          icon: IconHome,
        },
      ]),
  { label: 'Dashboard', href: `${userStore.aiAppUrl}/strategies`, icon: IconStrategies },
  {
    label: 'Strategy Generator',
    href: `${userStore.aiAppUrl}/create-strategy`,
    icon: IconGenerator,
  },
  { label: 'AI Indicators', href: `${userStore.aiAppUrl}/create-indicator`, icon: IconIndicators },
  {
    label: 'Code Transformer',
    href: `${userStore.aiAppUrl}/code-transformer`,
    icon: IconTransformer,
  },
  { label: 'AI Trading', href: `${userStore.aiAppUrl}/trading-ai`, icon: IconAiTrading },
]);
const visibleNavItems = computed(() => navItems.value.filter((item) => item.visible ?? true));

// Close the drawer when the viewport grows past the phone layout.
watch(isMobile, (mobile) => {
  if (!mobile) drawerVisible.value = false;
});
</script>

<template>
  <header class="sticky top-0 z-50">
    <div class="flex h-14 lg:h-auto items-center bg-card border-b border-border px-3 lg:p-3 backdrop-blur-sm">
      <RouterLink class="lg:ms-2 flex flex-row items-center pe-2 shrink-0" exact to="/">
        <img class="h-7 w-7 lg:h-8 lg:w-8 mr-3 lg:mr-4 align-middle invert dark:invert-0" src="@/assets/xiomex-logo-min.png" alt="Home Logo" />
        <span class="text-xl text-nowrap font-bold text-brand-gradient">Labs</span>
      </RouterLink>

      <nav v-if="!isMobile" class="flex items-center gap-6 ms-8 font-bold" aria-label="Main">
        <a
          v-for="link in externalLinks"
          :key="link.label"
          class="text-muted-foreground hover:text-foreground transition-colors text-nowrap"
          :href="link.href"
          >{{ link.label }}</a
        >
        <RouterLink
          v-for="item in visibleNavItems"
          :key="item.to"
          :to="item.to"
          class="text-muted-foreground hover:text-foreground transition-colors"
          active-class="text-foreground"
        >
          {{ item.label }}
        </RouterLink>
        <ThemeSelect />
      </nav>

      <!-- Phone: menu button + drawer -->
      <Button
        v-if="isMobile"
        variant="ghost"
        size="icon"
        class="ms-auto -me-1 text-muted-foreground hover:text-foreground"
        aria-label="Open menu"
        :aria-expanded="drawerVisible"
        @click="drawerVisible = true"
      >
        <i-mdi-menu class="h-6 w-6" />
      </Button>
    </div>

    <Drawer
      v-if="isMobile"
      v-model:visible="drawerVisible"
      position="right"
      :pt="{ root: { class: 'w-[min(20rem,85vw)]! bg-card text-card-foreground border-l border-border' } }"
    >
      <template #container="{ closeCallback }">
        <div class="flex h-full flex-col text-start">
          <div class="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
            <span class="text-xl font-bold text-brand-gradient">Labs</span>
            <Button
              variant="ghost"
              size="icon"
              class="-me-2 text-muted-foreground hover:text-foreground"
              aria-label="Close menu"
              @click="closeCallback"
            >
              <i-mdi-close class="h-6 w-6" />
            </Button>
          </div>

          <nav class="flex flex-col gap-1 overflow-y-auto p-2" aria-label="Main">
            <RouterLink
              v-for="item in visibleNavItems"
              :key="item.to"
              :to="item.to"
              class="flex h-12 items-center gap-3 rounded-lg px-3 font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              active-class="bg-secondary text-foreground!"
              @click="drawerVisible = false"
            >
              <component :is="item.icon" class="h-5 w-5 shrink-0" />
              {{ item.label }}
            </RouterLink>
            <div class="mx-3 my-1 h-px bg-border"></div>
            <a
              v-for="link in externalLinks"
              :key="link.label"
              :href="link.href"
              class="flex h-12 items-center gap-3 rounded-lg px-3 font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              @click="drawerVisible = false"
            >
              <component :is="link.icon" class="h-5 w-5 shrink-0" />
              {{ link.label }}
            </a>
          </nav>

          <div class="mt-auto flex flex-col gap-2 border-t border-border p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <Select
              v-if="botStore.botCount > 1"
              :model-value="botStore.selectedBotObj"
              size="small"
              :options="botStore.availableBotsSorted"
              @update:model-value="botStore.selectBot($event.botId)"
            >
              <template #value="{ value }">
                <BotEntry :bot="value" :no-buttons="true" />
              </template>
              <template #option="{ option }">
                <BotEntry :bot="option" :no-buttons="true" />
              </template>
            </Select>
            <div class="flex items-center justify-between">
              <ThemeSelect show-text />
              <span class="text-xs text-muted-foreground">v{{ settingsStore.uiVersion }}</span>
            </div>
          </div>
        </div>
      </template>
    </Drawer>
  </header>
</template>
